import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Menu,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconDotsVertical } from "@tabler/icons-react";
import { useState } from "react";
import { usePermission } from "@how/app-kit";
import {
  type EmailSender,
  useDeleteEmailSender,
  useEmailSenders,
  useRequestSenderVerification,
  useSaveEmailSender,
  useSetDefaultEmailSender,
} from "./sender-queries";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Email senders (ADR 0035 question 6, owner 2026-09-26; the Planning Center model): who a church's email appears to
 * come from and where replies go. The underlying address is the platform's; the sender is a name and a reply-to,
 * confirmed by the owner of that address through a link HoW emails them. A campaign picks one; the default is used
 * when it does not. Needs communications.read to see, communications.send to change.
 */
export function EmailSendersCard() {
  const canRead = usePermission("communications.read");
  const canSend = usePermission("communications.send", { write: true });
  const senders = useEmailSenders();
  const save = useSaveEmailSender();
  const remove = useDeleteEmailSender();
  const makeDefault = useSetDefaultEmailSender();
  const resend = useRequestSenderVerification();
  const [name, setName] = useState("");
  const [replyTo, setReplyTo] = useState("");

  if (canRead.data !== true) return null;
  const rows = senders.data ?? [];
  const error = save.error ?? remove.error ?? makeDefault.error ?? resend.error;

  return (
    <Card>
      <Stack gap="sm">
        <div>
          <Title order={4}>Email senders</Title>
          <Text size="sm" c="dimmed" maw={600}>
            Who an email appears to come from, and where replies go. A youth leader can send as
            themselves; the church can send as itself. The person who owns the reply-to address
            confirms it by email before it can be used. The sending address itself is the
            platform's, so nothing needs setting up with your email provider.
          </Text>
        </div>
        {rows.length === 0 ? (
          <Text size="sm" c="dimmed">
            No senders yet. Email campaigns need one.
          </Text>
        ) : (
          <Stack gap="xs">
            {rows.map((s) => (
              <SenderRow
                key={s.id}
                sender={s}
                canSend={canSend.data === true}
                onDefault={() => {
                  makeDefault.mutate(s.id);
                }}
                onResend={() => {
                  resend.mutate(s.id);
                }}
                onDelete={() => {
                  remove.mutate(s.id);
                }}
              />
            ))}
          </Stack>
        )}
        {canSend.data === true ? (
          <>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <TextInput
                label="Name people see"
                placeholder="Youth ministry, or a person's name"
                value={name}
                onChange={(event) => {
                  setName(event.currentTarget.value);
                }}
              />
              <TextInput
                label="Reply-to address"
                description="A confirmation goes to this address."
                placeholder="youth@yourchurch.org"
                type="email"
                value={replyTo}
                onChange={(event) => {
                  setReplyTo(event.currentTarget.value);
                }}
              />
            </SimpleGrid>
            <Group>
              <Button
                loading={save.isPending}
                disabled={name.trim() === "" || !EMAIL.test(replyTo.trim())}
                onClick={() => {
                  save.mutate(
                    { senderId: null, name: name.trim(), replyTo: replyTo.trim() },
                    {
                      onSuccess: () => {
                        setName("");
                        setReplyTo("");
                      },
                    },
                  );
                }}
              >
                Add sender
              </Button>
            </Group>
          </>
        ) : (
          <Text size="xs" c="dimmed">
            Staff who can send campaigns add, confirm and remove senders.
          </Text>
        )}
        {error ? (
          <Alert color="red" variant="light">
            {error.message}
          </Alert>
        ) : null}
      </Stack>
    </Card>
  );
}

function SenderRow({
  sender,
  canSend,
  onDefault,
  onResend,
  onDelete,
}: {
  readonly sender: EmailSender;
  readonly canSend: boolean;
  readonly onDefault: () => void;
  readonly onResend: () => void;
  readonly onDelete: () => void;
}) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <div>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500}>
            {sender.name}
          </Text>
          {sender.isDefault ? (
            <Badge size="sm" variant="light">
              Default
            </Badge>
          ) : null}
          {sender.verifiedAt === null ? (
            <Badge size="sm" variant="light" color="orange">
              Waiting for confirmation
            </Badge>
          ) : (
            <Badge size="sm" variant="light" color="teal">
              Confirmed
            </Badge>
          )}
        </Group>
        <Text size="xs" c="dimmed">
          Replies go to {sender.replyTo}
        </Text>
      </div>
      {canSend ? (
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon variant="subtle" color="gray" aria-label={`Actions for ${sender.name}`}>
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            {sender.verifiedAt !== null && !sender.isDefault ? (
              <Menu.Item onClick={onDefault}>Make the default</Menu.Item>
            ) : null}
            {sender.verifiedAt === null ? (
              <Menu.Item onClick={onResend}>Send the confirmation again</Menu.Item>
            ) : null}
            <Menu.Item color="red" onClick={onDelete}>
              Remove
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      ) : null}
    </Group>
  );
}
